import { fetchLoadColumnDetail } from "api/main.home";
import { fetchPreviewReviewDetail } from "api/review";
import { clipboardToastStatus } from "atoms/review.state";
import { LinkIcon } from "cssReset/icon";
import { ContentDTO } from "interface/interface.main.home";
import { ReviewDetailProps } from "interface/interface.review";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import styled from "styled-components";

interface ModalProps {
  handleModalClose: () => void;
  type?: string;
}

type SharedContentData = ReviewDetailProps | ContentDTO;

const ModalShareBox = ({ handleModalClose, type = "review" }: ModalProps) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<SharedContentData | null>(null);
  const setClipboard = useSetRecoilState(clipboardToastStatus);

  useEffect(() => {
    if (type === "column") {
      loadColumnDetail();
    } else {
      loadPreviewDetail();
    }
  }, [id]);

  const loadPreviewDetail = async () => {
    try {
      if (!id) return; // id가 없으면 함수 실행 중단
      const response = await fetchPreviewReviewDetail(Number(id));

      if (!response?.result) {
        // id가 유효하지 않으면 404 페이지로 이동
        navigate("/404");
        return;
      }

      setData(response.result);
    } catch (error) {
      console.error(error);
    }
  };

  const loadColumnDetail = async () => {
    try {
      if (!id) return;

      const response = await fetchLoadColumnDetail(Number(id));

      if (!response?.result) {
        // id가 유효하지 않으면 404 페이지로 이동
        navigate("/404");
        return;
      }

      setData(response.result);
    } catch (error) {
      throw new Error();
    }
  };

  const getContentId = (data: SharedContentData): number => {
    if ("contentsId" in data) return data.contentsId;
    return data.reviewId;
  };

  const shareToKakao = () => {
    if (!data) return;

    const isColumn = type === "column";
    const id = getContentId(data);
    const urlPath = isColumn ? `/column/detail/${id}` : `/review/detail/${id}`;

    Kakao.Share.sendCustom({
      templateId: 119524,
      templateArgs: {
        title: data.title,
        contents: data.contents,
        THU: "https://old.sombat.co.kr" + data.files?.[0]?.accessUrl,
        like: !isColumn ? (data as ReviewDetailProps).reviewLikeCount : 0,
        comment: !isColumn ? (data as ReviewDetailProps).reviewCommentCount : 0,
        view: data.views,
        url: urlPath,
      },
    });

    handleModalClose();
  };

  const shareFaceBook = () => {
    if (!data) return;

    const id = getContentId(data);
    const isColumn = type === "column";

    const shareUrl = isColumn
      ? `https://www.facebook.com/sharer/sharer.php?u=https://old.sombat.co.kr/backend/preview/share/column/${id}`
      : `https://www.facebook.com/sharer/sharer.php?u=https://old.sombat.co.kr/backend/preview/share/review/${id}`;

    const appShareUrl = isColumn
      ? `https://old.sombat.co.kr/backend/preview/share/column/${id}`
      : `https://old.sombat.co.kr/backend/preview/share/review/${id}`;

    const payload = {
      type: "buttonClicked", // 고정된 메시지 타입
      platform: "facebook", // 공유 플랫폼 정보
      contentType: type, // column 또는 review
      url: appShareUrl, // 실제 공유 URL
    };

    // ✅ iOS WebView
    if (window.webkit?.messageHandlers?.buttonClicked) {
      window.webkit.messageHandlers.buttonClicked.postMessage(payload);
    }
    // ✅ Android WebView
    else if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(payload));
    }
    // ✅ 일반 브라우저 (PC 또는 모바일 웹)
    else {
      window.open(shareUrl, "_blank");
    }

    handleModalClose();
  };

  const linkCopyHandler = async () => {
    try {
      if (!data) return;
      const id = getContentId(data);
      const shareUrl =
        type === "column"
          ? `https://old.sombat.co.kr/column/detail/${id}`
          : `https://old.sombat.co.kr/preview/review/detail/${id}`;

      await navigator.clipboard.writeText(shareUrl);
      // console.log("링크가 복사되었습니다!");
    } catch (err) {
      console.error("복사에 실패했어요. 다시 시도해 주세요.");
    } finally {
      setClipboard(true);
      handleModalClose();
    }
  };

  return (
    <BoxInner>
      <ShareItemBox>
        <li onClick={shareToKakao}>
          <span className="icon-box">
            <img src="/assets/icon/SNS/kakaotalk/Default.png" alt="" />
          </span>
          카카오톡 공유하기
        </li>
        <li onClick={shareFaceBook}>
          <span className="icon-box">
            <img
              src="/assets/icon/SNS/Facebook/Facebook - Original.png"
              alt=""
            />
          </span>
          페이스북 공유하기
        </li>
        <li onClick={linkCopyHandler}>
          <span className="icon-box">
            <LinkIcon />
          </span>
          공유링크 복사하기
        </li>
      </ShareItemBox>
    </BoxInner>
  );
};

export default ModalShareBox;

const BoxInner = styled.div``;

const ShareItemBox = styled.ul`
  li {
    display: flex;
    padding: 1.6rem 1rem;
    gap: 1rem;
    align-items: center;
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 2rem;
    letter-spacing: -0.06rem;
    border-bottom: 1px solid #ececec;
    .icon-box {
      display: flex;
      width: 2.4rem;
      height: 2.4rem;
      img,
      svg {
        width: 100%;
        height: 100%;
      }
    }
  }
`;
